import React from 'react'
import actions from 'redux/actions'
import useGlobals from 'sb-hooks/useGlobals'
import { useConnect } from 'sb-redux-hook'
import { useEffect, useCallback, useState, useMedia, useMemo } from 'sb-hooks'
import { mixpanel, scrollToElement, constants, getIsNewSubscribeFlowEnabled } from 'common/helpers'
import { ShippingType, creditCardForm } from 'pages/paymentNew/SubscribePage/util/forms'

import { Box } from 'components/_new/layout'
import Text from 'components/_new/typography/Text/Text'
import Modal from 'components/modal/Modal/Modal'
import { Button } from 'components/_new/controls'

import messages from './messages'
import s from './PrepaidCardWithDiscountModal.scss'


type PrepaidCardWithDiscountModalProps = {
  name: string
  data: {
    inquireValues: ShippingType & {
      plan?: string
      offer?: string
      couponCode?: string
      couponType?: string
      coupon?: { id: string }
      subscriptionCase?: string
    }
    handleProceed?: () => Promise<void>
  }
}

const couponCode = 'CARDFAILED'


const PrepaidCardWithDiscountModal: React.FunctionComponent<PrepaidCardWithDiscountModalProps> = (props) => {
  const { name, data: { inquireValues, handleProceed } } = props

  const { isMobile, isTablet } = useMedia()

  const isProceedWithPrepaidCardEnabled = useGlobals(({ getFeatureToggle }) => ({
    isProceedWithPrepaidCardEnabled: getFeatureToggle(constants.features.proceedWithPrepaidCard),
  }))

  const { gender, selectedPlan, isNewSubscribeFlowEnabled } = useConnect({
    gender: 'models.me.gender',
    selectedPlan: 'view.paymentPage.selectedPlan',
    isNewSubscribeFlowEnabled: getIsNewSubscribeFlowEnabled,
  })

  const [ isWaiting, setWaiting ] = useState(false)

  useEffect(() => {
    mixpanel.track(constants.analyticsEvents.cardFailed)
  }, [])

  const closeModal = useCallback(() => {
    actions.models.modals.close(name)
  }, [ name ])

  const applyCouponOldFlow = useCallback(() => {
    setWaiting(true)

    actions.view.paymentPage.fetchDiscount({ code: couponCode })
      .then(
        () => actions.view.paymentPage.inquireSubscription(inquireValues),
        () => Promise.reject()
      )
      .then(
        () => {
          closeModal()
          // Scroll to credit card form
          scrollToElement('creditCardForm', true)
          // Clear credit card form fields
          actions.view.paymentPage.changeEventForCleanCreditCardForm(true)
        },
        () => setWaiting(false)
      )
  }, [ inquireValues, closeModal ])

  const applyCouponNewFlow = useCallback(() => {
    setWaiting(true)

    actions.view.paymentPageNew.setInquireFetchingStatus(true)

    actions.view.paymentPageNew.inquireSubscription({
      ...inquireValues,
      couponCode,
      plan: selectedPlan,
    })
      .then((result) => {
        if (result) {
          const { coupon: { code, errorMessage }, bill: { offer } } = result

          actions.view.paymentPageNew.setInquireAndBillData({
            inquireData: result,
            isFreeTrial: false,
            selectedPlan,
            gender,
          })

          if (code && !errorMessage) {
            actions.misc.cookies.setItem(constants.cookieNames.discountCoupon, code)
            actions.misc.cookies.setItem(constants.cookieNames.newPaymentCouponCode, offer || code)
          }

          closeModal()
          // Scroll to credit card form
          scrollToElement('creditCardForm', true)
          // Clear credit card form fields
          creditCardForm.unsetValues()
        }
        else {
          setWaiting(false)
          actions.view.paymentPageNew.setInquireFetchingStatus(false)
        }
      })
  }, [ gender, selectedPlan, inquireValues, closeModal ])

  const applyCoupon = useMemo(() => (
    isNewSubscribeFlowEnabled && !isTablet ? applyCouponNewFlow : applyCouponOldFlow
  ), [ isNewSubscribeFlowEnabled, isTablet, applyCouponNewFlow, applyCouponOldFlow ])

  const handleProceedClick = useCallback(() => {
    closeModal()
    actions.models.ui.showRequestLoader()

    handleProceed()
      .finally(() => actions.models.ui.hideRequestLoader())
  }, [ closeModal, handleProceed ])

  return (
    <Modal
      className={s.prepaidCardWithDiscountModal}
      name={name}
      title={messages.title}
      onClose={closeModal}
    >
      <Box className={s.text} pt={8} pb={24} mb={24}>
        <Text
          message={messages.text}
          size="16-24"
          color="black"
        />
      </Box>
      <Button
        title={messages.buttonTitle}
        color="brand"
        size="sm"
        loading={isWaiting}
        fullWidthOnMobile
        dataTestId="applyCouponButton"
        onClick={applyCoupon}
      />
      {
        Boolean(isProceedWithPrepaidCardEnabled && handleProceed) && (
          <Box
            mt={isMobile ? '12' : null}
            ml={isMobile ? null : '16'}
            inline={!isMobile}
          >
            <Button
              title={messages.proceedButton}
              color="brandBorder"
              size="sm"
              loading={isWaiting}
              fullWidthOnMobile
              dataTestId="proceedWithPrepaidCardButton"
              onClick={handleProceedClick}
            />
          </Box>
        )
      }
    </Modal>
  )
}

PrepaidCardWithDiscountModal.defaultProps = {
  name: constants.modals.prepaidCard.PrepaidCardWithDiscount,
}


export default PrepaidCardWithDiscountModal
